import { KeyboardEvent, useCallback } from "react";
import {
  Box,
  TableCell,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import ExpandIcon from "../../../../assets/img/more.svg";
import LessIcon from "../../../../assets/img/less.svg";
import { hierarchyTableLayout } from "../../Constants/constants";
import { hierarchyTableStyles } from "../../Styles/hierarchyTableStyles";
import type {
  BilledHoursCellsProps,
  HierarchyTableRowProps,
} from "../../Types/types";
import { formatPercent, hasChildren } from "./utils";
import { formatValue } from "../../../OperationsDashboard/utils";

const BilledHoursCells = ({ level, breakdown }: BilledHoursCellsProps) => (
  <>
    <TableCell
      sx={hierarchyTableStyles.billedHoursMetricCell(level, "offshore")}
    >
      {formatValue(breakdown.offshore)}
    </TableCell>
    <TableCell sx={hierarchyTableStyles.billedHoursMetricCell(level, "onsite")}>
      {formatValue(breakdown.onsite)}
    </TableCell>
    <TableCell sx={hierarchyTableStyles.billedHoursMetricCell(level, "total")}>
      {formatValue(breakdown.total)}
    </TableCell>
  </>
);

export const HierarchyTableRow = ({
  node,
  depth,
  isExpanded,
  onToggle,
  isLoading,
}: HierarchyTableRowProps) => {
  const expandable = hasChildren(node);
  const { metrics } = node;

  const handleHierarchyToggle = useCallback(() => {
    if (expandable && !isLoading) {
      onToggle(node.entity_id, node.level);
    }
  }, [expandable, node.entity_id, node.level, onToggle, isLoading]);

  const handleHierarchyKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTableCellElement>) => {
      if (!expandable || isLoading) {
        return;
      }

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onToggle(node.entity_id, node.level);
      }
    },
    [expandable, node.entity_id, node.level, onToggle, isLoading],
  );

  let hierarchyAriaLabel: string | undefined;
  if (expandable) {
    hierarchyAriaLabel = isExpanded
      ? `Collapse ${node.label}`
      : `Expand ${node.label}`;
  }

  const renderHierarchyIcon = () => {
    if (isLoading) {
      return (
        <CircularProgress
          size={16}
          sx={{ mr: 1, display: "inline-flex", verticalAlign: "middle" }}
        />
      );
    }

    if (expandable) {
      const expandIconSrc = isExpanded ? LessIcon : ExpandIcon;

      return (
        <Box
          component="img"
          src={expandIconSrc}
          alt="expand/collapse"
          sx={hierarchyTableStyles.expandIcon}
        />
      );
    }

    return <Box sx={hierarchyTableStyles.expandIconPlaceholder} />;
  };

  return (
    <TableRow hover>
      <TableCell
        sx={hierarchyTableStyles.hierarchyCell(depth, expandable)}
        onClick={expandable ? handleHierarchyToggle : undefined}
        onKeyDown={expandable ? handleHierarchyKeyDown : undefined}
        tabIndex={expandable ? 0 : undefined}
        aria-expanded={expandable ? isExpanded : undefined}
        aria-label={hierarchyAriaLabel}
      >
        <Box sx={hierarchyTableStyles.hierarchyLabelRow}>
          {renderHierarchyIcon()}
          <Typography
            component="span"
            noWrap
            title={node.label}
            sx={hierarchyTableStyles.hierarchyLabelText(node.level)}
          >
            {node.label}
          </Typography>
        </Box>
      </TableCell>
      <TableCell
        sx={hierarchyTableStyles.dataCell(node.level, {
          noBorderRight: true,
          noBorderTop: true,
          noBorderLeft: true,
          align: "right",
          width: hierarchyTableLayout.revenueColWidth,
        })}
      >
        {formatValue(metrics.revenue_cm1)}
      </TableCell>
      <TableCell
        sx={hierarchyTableStyles.dataCell(node.level, {
          noBorderRight: true,
          noBorderTop: true,
          noBorderLeft: true,
          align: "right",
          width: hierarchyTableLayout.revenueColWidth,
        })}
      >
        {formatValue(metrics.revenue_period)}
      </TableCell>
      <TableCell
        sx={hierarchyTableStyles.dataCell(node.level, {
          noBorderTop: true,
          noBorderLeft: true,
          align: "right",
          width: hierarchyTableLayout.revenueColWidth,
        })}
      >
        {formatPercent(metrics.gm_percent)}
      </TableCell>
      <BilledHoursCells
        level={node.level}
        breakdown={metrics.billed_hours_cm1}
      />
      <BilledHoursCells
        level={node.level}
        breakdown={metrics.billed_hours_period}
      />
    </TableRow>
  );
};
